<template>
  <div class="nueva-propuesta-container p-2">
    <!-- Header -->
    <div class="d-flex align-items-center mb-2">
      <b-link @click="$router.push({ name: 'talentos' })" class="text-blue mr-1">
        <feather-icon icon="ArrowLeftIcon" size="16" class="mr-25" />
        <span>Talentos</span>
      </b-link>
    </div>

    <h4 class="mb-25">Agregar propuesta de talento</h4>
    <p class="text-muted mb-2">Busca al creador por correo electrónico y completa su propuesta.</p>

    <!-- ── PASO 1: Buscar talento ── -->
    <b-card class="section-card mb-2">
      <div class="section-header mb-1">
        <div class="step-badge">1</div>
        <h6 class="mb-0 ml-75">Buscar talento</h6>
      </div>

      <b-input-group>
        <b-form-input
          v-model="search_email"
          placeholder="Correo electrónico del talento"
          type="email"
          :disabled="search_status === 'found'"
          :state="search_email ? isValidEmail(search_email) : null"
          @keyup.enter="searchUser"
        />
        <b-input-group-append>
          <b-button
            v-if="search_status !== 'found'"
            variant="primary"
            :disabled="!isValidEmail(search_email) || searching"
            @click="searchUser"
          >
            <b-spinner v-if="searching" small class="mr-50" />
            <feather-icon v-else icon="SearchIcon" size="14" class="mr-50" />
            Buscar
          </b-button>
          <b-button v-else variant="outline-secondary" @click="resetSearch">
            <feather-icon icon="XIcon" size="14" class="mr-25" />
            Cambiar
          </b-button>
        </b-input-group-append>
      </b-input-group>

      <!-- Resultado: encontrado -->
      <div v-if="search_status === 'found'" class="user-found-card mt-1">
        <b-avatar :src="talent.avatar" size="38" class="mr-1" />
        <div>
          <p v-if="talent.first_name" class="mb-0 font-weight-bold">{{ talent.first_name }} {{ talent.last_name }}</p>
          <p v-else class="mb-0 font-weight-bold text-muted">Sin nombre</p>
          <small class="text-muted">{{ talent.email }}</small>
        </div>
        <b-badge variant="light-success" class="ml-auto">Encontrado</b-badge>
      </div>

      <!-- Resultado: no encontrado, o encontrado sin nombre -->
      <div v-if="needs_user_data" class="mt-1">
        <b-alert v-if="search_status === 'not_found'" show variant="warning" class="mb-1 py-75 px-1">
          <feather-icon icon="AlertCircleIcon" size="14" class="mr-50" />
          No encontramos una cuenta con ese correo. Puedes crear el talento manualmente.
        </b-alert>
        <b-alert v-else show variant="warning" class="mb-1 py-75 px-1">
          <feather-icon icon="AlertCircleIcon" size="14" class="mr-50" />
          Este creador no tiene su nombre registrado. Completa sus datos para continuar.
        </b-alert>

        <div class="d-flex align-items-center mb-1">
          <b-avatar :src="new_user.avatar_preview || (talent && talent.avatar) || ''" size="64" class="mr-1" />
          <div>
            <p class="mb-25 font-weight-bold">Foto de perfil</p>
            <div class="d-flex align-items-center">
              <b-button variant="outline-primary" size="sm" class="mr-50" @click="$refs.avatar_input.click()">
                <feather-icon icon="UploadIcon" size="14" class="mr-25" />
                {{ new_user.avatar ? 'Cambiar foto' : 'Subir foto' }}
              </b-button>
              <b-button v-if="new_user.avatar" variant="flat-danger" size="sm" @click="removeAvatar">
                Quitar
              </b-button>
            </div>
            <small v-if="avatar_error" class="text-danger d-block mt-25">{{ avatar_error }}</small>
            <small v-else class="text-muted d-block mt-25">JPG o PNG, máximo 5MB</small>
            <input
              ref="avatar_input"
              type="file"
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              class="d-none"
              @change="onAvatarChange"
            />
          </div>
        </div>

        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Nombre *" label-for="input-first-name">
              <b-form-input id="input-first-name" v-model="new_user.first_name" placeholder="Nombre" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Apellido *" label-for="input-last-name">
              <b-form-input id="input-last-name" v-model="new_user.last_name" placeholder="Apellido" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Correo electrónico *" label-for="input-email">
              <b-form-input id="input-email" v-model="new_user.email" type="email" disabled />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="País" label-for="input-country">
              <v-select
                id="input-country"
                v-model="new_user.country"
                :options="country_options"
                :reduce="c => c.value"
                label="text"
                placeholder="Buscar país..."
                :clearable="false"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Banner propuesta completa -->
    <b-alert v-if="proposal_complete" show variant="success" class="mb-2 d-flex align-items-center">
      <feather-icon icon="CheckCircleIcon" size="16" class="mr-75" />
      Este talento ya tiene una propuesta completa. Puedes asignarla directamente a tu agencia.
    </b-alert>

    <div :class="{ 'section-locked': proposal_complete }">

    <!-- ── PASO 2: Muestras ── -->
    <b-card class="section-card mb-2" :class="{ 'section-disabled': !search_status }">
      <div class="section-header mb-1">
        <div class="step-badge">2</div>
        <h6 class="mb-0 ml-75">Muestras</h6>
        <small class="text-muted ml-75">Imágenes o videos de ejemplo (máx. 10)</small>
      </div>

      <!-- Drop zone -->
      <div
        v-if="samples.length < 10 && !proposal_complete"
        class="samples-dropzone"
        :class="{ 'samples-dropzone--drag': is_dragging_over }"
        @dragover.prevent="is_dragging_over = true"
        @dragleave.prevent="is_dragging_over = false"
        @drop.prevent="onDrop"
        @click="$refs.file_input.click()"
      >
        <feather-icon icon="CameraIcon" size="32" class="text-muted mb-50" />
        <p class="mb-0 text-muted">Arrastra archivos o <span class="text-primary">elige desde tu dispositivo</span></p>
        <small class="text-muted">JPG, PNG, MP4, MOV</small>
        <input
          ref="file_input"
          type="file"
          accept=".jpeg,.jpg,.png,.mp4,.mov"
          multiple
          class="d-none"
          @change="onFileChange"
        />
      </div>

      <p v-if="samples.length === 0 && search_status === 'found' && total_images === 0" class="text-danger small mb-1 mt-50">
        <feather-icon icon="AlertCircleIcon" size="12" class="mr-25" />
        Se requiere al menos una imagen o video para guardar la propuesta.
      </p>
      <p v-if="samples.length >= 10" class="text-danger small mb-1">Límite de 10 archivos alcanzado.</p>

      <!-- Lista de muestras -->
      <div v-if="samples.length > 0" class="samples-list mt-1">
        <div v-for="(sample, i) in samples" :key="i" class="sample-row">
          <div class="d-flex align-items-center">
            <div class="sample-thumb mr-1">
              <video v-if="sample.is_video" :src="sample.preview" class="sample-thumb-media" />
              <img v-else :src="sample.preview" class="sample-thumb-media" />
            </div>
            <div>
              <p class="mb-0 small font-weight-bold text-truncate" style="max-width:200px">{{ sample.name }}</p>
              <small class="text-muted">{{ sample.is_video ? 'Video' : 'Imagen' }}</small>
            </div>
          </div>
          <b-button v-if="!proposal_complete" variant="flat-danger" size="sm" class="btn-icon" @click="removeSample(i)">
            <feather-icon icon="TrashIcon" size="14" />
          </b-button>
        </div>
      </div>

      <div v-if="samples.length === 0" class="text-center text-muted small mt-50">
        Es necesario añadir al menos una muestra.
      </div>
    </b-card>

    <!-- ── PASO 3: Propuesta ── -->
    <b-card class="section-card mb-2" :class="{ 'section-disabled': !search_status }">
      <div class="section-header mb-1">
        <div class="step-badge">3</div>
        <h6 class="mb-0 ml-75">Propuesta</h6>
      </div>

      <b-row>
        <b-col cols="12">
          <b-form-group label="Título de la propuesta *" label-for="input-proposal-title">
            <b-form-input
              id="input-proposal-title"
              v-model="proposal.title"
              placeholder="Ej. Fotografía de producto para redes sociales"
              :disabled="!search_status || proposal_complete"
              :state="proposal.title ? proposal.title.length < 150 : null"
            />
            <b-form-invalid-feedback>Máximo 150 caracteres</b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group label="Descripción *" label-for="input-proposal-desc">
            <b-form-textarea
              id="input-proposal-desc"
              v-model="proposal.description"
              placeholder="Describe lo que ofrece este talento, su estilo, experiencia y lo que incluye la propuesta..."
              rows="4"
              :disabled="!search_status || proposal_complete"
              :state="proposal.description ? description_valid : null"
            />
            <small class="text-muted float-right">{{ (proposal.description || '').length }}/2000</small>
            <b-form-invalid-feedback>Mínimo 20 caracteres y máximo 2000</b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- ── PASO 4: Deals ── -->
    <b-card class="section-card mb-2" :class="{ 'section-disabled': !search_status }">
      <div class="section-header mb-1">
        <div class="step-badge">4</div>
        <h6 class="mb-0 ml-75">Servicios (deals)</h6>
        <b-button
          v-if="search_status && !proposal_complete && can_add_deal"
          size="sm"
          variant="outline-primary"
          class="ml-auto"
          @click="addDeal"
        >
          <feather-icon icon="PlusIcon" size="14" class="mr-25" />
          Agregar servicio
        </b-button>
        <small v-if="search_status && !proposal_complete && !can_add_deal" class="ml-auto text-muted">Máximo 4 servicios</small>
      </div>

      <!-- Deals existentes con el diseño de dealRadio -->
      <b-row class="m-0" v-if="saved_deals.length > 0">
        <deal-radio
          v-for="(deal, i) in saved_deals"
          :key="deal.uuid || i"
          :deal="deal"
          :index="i"
          :is-last="i + 1 === saved_deals.length"
          :is_avaible_to_editing="false"
          :is_brandme_talent="deal.is_brandme_talent"
          :loaded_ia="false"
          class="mb-1"
        />
      </b-row>

      <!-- Formulario para nuevos deals -->
      <div v-for="(deal, i) in new_deals" :key="`new-${i}`" class="deal-card mb-1">
        <div class="deal-card-header">
          <span class="font-weight-bold">Nuevo servicio {{ i + 1 }}</span>
          <b-button variant="flat-danger" size="sm" class="btn-icon" @click="removeDeal(i)">
            <feather-icon icon="Trash2Icon" size="14" />
          </b-button>
        </div>
        <b-row class="px-1 pb-1">
          <b-col cols="12">
            <b-form-group label="Cuentas conectadas *" :label-for="`deal-networks-${i}`">
              <div class="network-chips" :id="`deal-networks-${i}`">
                <div
                  v-for="net in talent_networks"
                  :key="net.uuid"
                  class="net-chip"
                  :class="{ 'net-chip--selected': deal.selected_networks.includes(net.uuid) }"
                  @click="toggleNetwork(deal, net.uuid)"
                >
                  <div class="net-icon" :class="getNetworkColor(net.network)">
                    <img :src="getNetworkIcon(net.network)" width="16" height="16" alt="" />
                  </div>
                  <div class="net-text">
                    <span class="net-name">{{ net.network }}</span>
                    <span class="net-user">@{{ net.username }}</span>
                  </div>
                  <div class="net-check">
                    <feather-icon icon="CheckIcon" size="10" />
                  </div>
                </div>
              </div>
              <small v-if="talent_networks.length === 0 && !new_network_form.show" class="text-muted d-block mb-50">No hay cuentas conectadas para este talento.</small>

              <!-- Formulario inline para agregar red nueva -->
              <div v-if="new_network_form.show" class="add-network-form mt-75">
                <div class="add-network-form-header">
                  <span class="font-weight-bold" style="font-size:13px">Agregar nueva red</span>
                  <b-button variant="flat-secondary" size="sm" class="btn-icon p-0" @click="new_network_form.show = false">
                    <feather-icon icon="XIcon" size="14" />
                  </b-button>
                </div>

                <div class="add-net-picker">
                  <div
                    v-for="net in available_networks"
                    :key="net.value"
                    class="add-net-option"
                    :class="{ 'add-net-option--selected': new_network_form.network === net.value }"
                    :title="net.text"
                    @click="new_network_form.network = net.value"
                  >
                    <div class="add-net-icon" :class="getNetworkColor(net.value)">
                      <img :src="getNetworkIcon(net.value)" width="14" height="14" alt="" />
                    </div>
                  </div>
                </div>

                <div class="add-net-fields">
                  <b-form-input
                    v-model="new_network_form.username"
                    placeholder="@usuario"
                    size="sm"
                  />
                  <b-form-input
                    v-model="new_network_form.followers"
                    placeholder="Seguidores (opcional)"
                    type="number"
                    min="0"
                    size="sm"
                  />
                </div>

                <div class="add-net-actions">
                  <b-button
                    size="sm"
                    variant="primary"
                    :disabled="!new_network_form.network || !new_network_form.username.trim() || adding_network"
                    @click="confirmAddNetwork"
                  >
                    <b-spinner v-if="adding_network" small class="mr-25" />
                    <feather-icon v-else icon="PlusIcon" size="12" class="mr-25" />
                    Agregar cuenta
                  </b-button>
                </div>
              </div>

              <b-button
                v-if="!new_network_form.show"
                variant="flat-primary"
                size="sm"
                class="mt-25 px-0"
                @click="new_network_form.show = true"
              >
                <feather-icon icon="PlusIcon" size="12" class="mr-25" />
                Agregar cuenta
              </b-button>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Nombre de la actividad (Deal) *" :label-for="`deal-title-${i}`">
              <b-form-input
                :id="`deal-title-${i}`"
                v-model="deal.title"
                placeholder="Ejemplo de Deal: Paquete 3 contenidos + Uso comercial"
                :state="deal.title.trim() ? true : (deal_errors[i] && deal_errors[i].title ? false : null)"
              />
              <b-form-invalid-feedback>El nombre del deal es requerido.</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Descripción de la actividad (Deal) *" :label-for="`deal-desc-${i}`">
              <b-form-textarea
                :id="`deal-desc-${i}`"
                v-model="deal.description"
                placeholder="Ejemplo de Descripción: Paquete 3 contenidos ( Fijo + 2 stories ) con uso por 60 días de los contenidos amplificado en red social a elegir"
                rows="3"
                :state="deal.description && deal.description.length >= 20 ? true : (deal.description && deal.description.length > 0 ? false : null)"
              />
              <b-form-invalid-feedback>Mínimo 20 caracteres.</b-form-invalid-feedback>
              <small class="text-muted float-right">{{ (deal.description || '').length }} caracteres</small>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Precio (en USD) *" :label-for="`deal-price-${i}`">
              <b-form-radio-group
                v-model="deal.price_type"
                :options="price_type_options"
                button-variant="outline-primary"
                size="sm"
                buttons
                class="mb-1"
              />
              <template v-if="deal.price_type === 'range'">
                <b-form-rating v-model="deal.range_price" no-border size="lg" class="price-range-rating">
                  <template #icon-empty>
                    <b-img src="@/assets/images/icons/dollar-empty.svg" class="icon-dollar"/>
                  </template>
                  <template #icon-full>
                    <b-img src="@/assets/images/icons/dollar-full.svg" class="icon-dollar"/>
                  </template>
                </b-form-rating>
                <small class="text-muted">El precio no se mostrará, solo el rango seleccionado.</small>
              </template>
              <template v-else>
              <b-input-group prepend="$">
                <b-form-input
                  :id="`deal-price-${i}`"
                  v-model="deal.price"
                  type="number"
                  min="10"
                  placeholder="0"
                  :state="deal.price ? parseFloat(deal.price) > 10 : null"
                />
              </b-input-group>
              <b-form-invalid-feedback :state="deal.price ? parseFloat(deal.price) > 10 : null">El monto mínimo debe ser de $ 10 USD.</b-form-invalid-feedback>
              <small v-if="!deal.price || parseFloat(deal.price) <= 10" class="text-muted">El monto mínimo debe ser de $ 10 USD</small>
              </template>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <div v-if="saved_deals.length === 0 && new_deals.length === 0" class="empty-deals text-center py-2 text-muted">
        <feather-icon icon="PackageIcon" size="32" class="mb-75 text-muted" />
        <p class="mb-0">Aún no hay servicios. Agrega al menos uno.</p>
      </div>
    </b-card>

    </div><!-- /section-locked -->

    <!-- ── Acciones ── -->
    <div class="d-flex justify-content-end mb-3">
      <b-button variant="outline-secondary" class="mr-1" @click="$router.push({ name: 'talentos' })">
        Cancelar
      </b-button>
      <b-button
        variant="primary"
        :disabled="!canSubmit || submitting"
        @click="submit"
      >
        <b-spinner v-if="submitting" small class="mr-50" />
        <feather-icon v-else :icon="proposal_complete ? 'BriefcaseIcon' : 'SaveIcon'" size="14" class="mr-50" />
        {{ proposal_complete ? 'Asignar a agencia' : 'Guardar propuesta' }}
      </b-button>
    </div>
  </div>
</template>

<script>
import talentosService from '@/services/talentos'
import { verifyAsset } from '@/libs/utils/urls'
import { getIconWhite, getIconColor } from '@/libs/utils/icons'
import { getCountries } from '@/libs/utils/others'
import vSelect from 'vue-select'
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BFormInput,
  BFormGroup,
  BFormTextarea,
  BInputGroup,
  BInputGroupAppend,
  BFormInvalidFeedback,
  BAvatar,
  BBadge,
  BAlert,
  BLink,
  BSpinner,
  BFormRadioGroup,
  BFormRating,
  BImg,
} from 'bootstrap-vue'
import DealRadio from '@/views/components/proposals/components/dealRadio.vue'

const EMPTY_DEAL = () => ({
  selected_networks: [],
  title: '',
  description: '',
  price: '',
  price_type: 'fixed',
  range_price: null,
})

const VIDEO_EXTS = ['mp4', 'mov']
const AVATAR_EXTS = ['jpg', 'jpeg', 'png']
const AVATAR_MAX_SIZE = 5 * 1024 * 1024

export default {
  name: 'NuevaPropuesta',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BFormInput,
    BFormGroup,
    BFormTextarea,
    BInputGroup,
    BInputGroupAppend,
    BFormInvalidFeedback,
    BAvatar,
    BBadge,
    BAlert,
    BLink,
    BSpinner,
    BFormRadioGroup,
    BFormRating,
    BImg,
    DealRadio,
    vSelect,
  },
  data() {
    return {
      search_email: '',
      searching: false,
      submitting: false,
      search_status: '',
      talent: null,
      is_dragging_over: false,

      new_user: {
        first_name: '',
        last_name: '',
        email: '',
        country: '',
        avatar: null,
        avatar_preview: '',
      },
      avatar_error: '',

      total_images: 0,
      followers_by_username: [],
      talent_networks_list: [],
      new_network_form: {
        show: false,
        network: null,
        username: '',
        followers: '',
      },
      adding_network: false,
      samples: [],

      proposal: {
        title: '',
        description: '',
      },

      saved_deals: [],
      new_deals: [],

    }
  },
  computed: {
    needs_user_data() {
      return this.search_status === 'not_found' ||
        (this.search_status === 'found' && !!this.talent && !this.talent.first_name)
    },
    proposal_complete() {
      return this.search_status === 'found' && this.total_images > 0
    },
    talent_networks() {
      return this.talent_networks_list
    },
    available_networks() {
      return [
        { value: 'instagram', text: 'Instagram' },
        { value: 'tiktok', text: 'TikTok' },
        { value: 'youtube', text: 'YouTube' },
        { value: 'twitter', text: 'X (Twitter)' },
        { value: 'facebook', text: 'Facebook' },
        { value: 'twitch', text: 'Twitch' },
        { value: 'snapchat', text: 'Snapchat' },
        { value: 'pinterest', text: 'Pinterest' },
        { value: 'linkedin', text: 'LinkedIn' },
        { value: 'spotify', text: 'Spotify / Podcast' },
        { value: 'telegram', text: 'Telegram' },
        { value: 'whatsapp', text: 'WhatsApp' },
        { value: 'blog', text: 'Blog / Web' },
      ]
    },
    price_type_options() {
      return [
        { value: 'fixed', text: 'Precio fijo' },
        { value: 'range', text: 'Rango de precio' },
      ]
    },
    can_add_deal() {
      return this.new_deals.length < 4
    },
    country_options() {
      return getCountries().map(c => ({ value: c['alpha-2'], text: c.name }))
    },
    deal_errors() {
      return this.new_deals.map(d => ({
        title: !d.title.trim(),
        networks: d.selected_networks.length === 0,
        description: !d.description || d.description.length < 20,
        price: d.price_type === 'range' ? !(d.range_price > 0) : (!d.price || parseFloat(d.price) <= 10),
      }))
    },
    deals_valid() {
      return this.new_deals.length > 0 && this.deal_errors.every(
        e => !e.title && !e.networks && !e.description && !e.price
      )
    },
    description_valid() {
      const length = (this.proposal.description || '').length
      return length >= 20 && length <= 2000
    },

    canSubmit() {
      const has_new_user_data = !!(this.new_user.first_name.trim() && this.new_user.last_name.trim())
      const has_user = this.needs_user_data ? has_new_user_data : this.search_status === 'found'

      if (this.proposal_complete) return has_user

      if (this.search_status === 'found' && this.total_images === 0) {
        const has_samples = this.samples.length > 0
        const has_proposal = this.proposal.title.trim() && this.description_valid
        return has_user && has_samples && has_proposal && this.deals_valid
      }

      const has_proposal = this.proposal.title && this.description_valid
      const has_deals = this.saved_deals.length > 0 || this.new_deals.length > 0
      return has_user && has_proposal && has_deals
    },
  },
  methods: {
    getNetworkIcon(network) {
      return getIconWhite(network)
    },
    getNetworkColor(network) {
      return getIconColor(network, false)
    },
    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },

    async searchUser() {
      if (!this.isValidEmail(this.search_email)) return
      this.searching = true
      this.search_status = ''

      try {
        const data = await talentosService.searchTalent(this.search_email)

        if (data.exists && data.proposal) {
          const p = data.proposal
          this.talent = {
            first_name: p.first_name,
            last_name: p.last_name,
            email: p.email,
            country: p.country || '',
            avatar: verifyAsset(p.avatar),
            details_proposal_uuid: p.details_proposal_uuid || null,
          }
          this.total_images = p.total_images || 0
          this.followers_by_username = p.followers_by_username || []
          this.proposal.title = p.title || ''
          this.proposal.description = p.description || ''
          this.samples = (p.images || []).map(img => ({
            name: img.media.split('/').pop(),
            preview: verifyAsset(img.preview || img.media),
            is_video: /\.(mp4|mov)$/i.test(img.media),
            uuid: img.uuid,
            file: null,
          }))
          this.search_status = 'found'

          if (!this.talent.first_name) {
            this.new_user.email = this.talent.email
            this.new_user.last_name = this.talent.last_name || ''
            this.new_user.country = this.talent.country || ''
          }

          // Siempre cargar deals existentes
          try {
            const deals_data = await talentosService.getDealsByVanity(p.vanity_name)
            const results = deals_data?.results ?? []
            this.saved_deals = results.map(d => ({
              uuid: d.uuid,
              user_networks: d.user_networks || [],
              title: d.title || '',
              description: d.description || '',
              price: d.price || '',
              days_delivery: d.days_delivery || 3,
              revision: d.revision || 1,
              licensing: d.licensing || null,
              features: d.features || [],
              is_brandme_talent: d.is_brandme_talent || false,
              range_price: d.range_price || null,
            }))
          } catch (_) {
            this.saved_deals = []
          }

          if (this.total_images === 0) {
            // Sin imágenes: cargar redes del talento para los nuevos deals
            try {
              const nets_data = await talentosService.getTalentUserNetworks(p.email)
              this.talent_networks_list = nets_data?.results ?? []
            } catch (_) {
              this.talent_networks_list = []
            }
          }
        } else {
          this.new_user.email = this.search_email
          this.search_status = 'not_found'
        }
      } catch (e) {
        this.new_user.email = this.search_email
        this.search_status = 'not_found'
      } finally {
        this.searching = false
      }
    },

    resetSearch() {
      this.search_status = ''
      this.talent = null
      this.total_images = 0
      this.followers_by_username = []
      this.talent_networks_list = []
      this.new_network_form = { show: false, network: null, username: '', followers: '' }
      this.removeAvatar()
      this.new_user = { first_name: '', last_name: '', email: '', country: '', avatar: null, avatar_preview: '' }
      this.proposal = { title: '', description: '' }
      this.samples = []
      this.saved_deals = []
      this.new_deals = []
    },

    isVideo(file) {
      const ext = file.name.split('.').pop().toLowerCase()
      return VIDEO_EXTS.includes(ext)
    },

    addFiles(files) {
      const remaining = 10 - this.samples.length
      Array.from(files).slice(0, remaining).forEach(file => {
        const is_video = VIDEO_EXTS.includes(file.name.split('.').pop().toLowerCase())
        const preview = URL.createObjectURL(file)
        this.samples.push({ name: file.name, preview, is_video, file })
      })
    },

    onFileChange(e) {
      this.addFiles(e.target.files)
      this.$refs.file_input.value = ''
    },

    onDrop(e) {
      this.is_dragging_over = false
      this.addFiles(e.dataTransfer.files)
    },

    onAvatarChange(e) {
      const file = e.target.files[0]
      this.$refs.avatar_input.value = ''
      if (!file) return

      const ext = file.name.split('.').pop().toLowerCase()
      const valid_type = ['image/jpeg', 'image/png'].includes(file.type) && AVATAR_EXTS.includes(ext)
      if (!valid_type) {
        this.avatar_error = 'Solo se permiten imágenes JPG o PNG.'
        return
      }
      if (file.size > AVATAR_MAX_SIZE) {
        this.avatar_error = 'La imagen no debe pesar más de 5MB.'
        return
      }

      this.removeAvatar()
      this.new_user.avatar = file
      this.new_user.avatar_preview = URL.createObjectURL(file)
    },

    removeAvatar() {
      if (this.new_user.avatar_preview) URL.revokeObjectURL(this.new_user.avatar_preview)
      this.new_user.avatar = null
      this.new_user.avatar_preview = ''
      this.avatar_error = ''
    },

    removeSample(index) {
      this.samples.splice(index, 1)
    },

    async confirmAddNetwork() {
      const { network, username, followers } = this.new_network_form
      if (!network || !username.trim()) return
      const parsed_followers = parseInt(followers, 10) || 0
      this.adding_network = true
      try {
        const email = this.talent ? this.talent.email : this.new_user.email
        const result = await talentosService.addTalentUserNetwork(
          email,
          network,
          username.trim(),
          parsed_followers
        )
        const new_net = {
          uuid: result?.user_network?.uuid || `manual-${Date.now()}`,
          network,
          username: username.trim(),
          readable_name: username.trim(),
          connected: false,
          followers: parsed_followers,
          avatar: null,
        }
        this.talent_networks_list.push(new_net)
        this.new_network_form = { show: false, network: null, username: '', followers: '' }
      } catch (e) {
        const backend_error = this.getBackendError(e)
        const message = 'No se pudo agregar la red social, intenta de nuevo.'
        this.$bvToast.toast(backend_error ? `${message} (${backend_error})` : message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.adding_network = false
      }
    },

    dealPayload(d) {
      const payload = {
        user_networks: d.selected_networks,
        title: d.title,
        description: d.description,
        price: parseFloat(d.price),
      }
      if (d.price_type === 'range') {
        payload.price = 0
        payload.is_brandme_talent = true
        payload.range_price = d.range_price
      }
      return payload
    },
    toggleNetwork(deal, uuid) {
      const idx = deal.selected_networks.indexOf(uuid)
      if (idx === -1) {
        deal.selected_networks.push(uuid)
      } else {
        deal.selected_networks.splice(idx, 1)
      }
    },

    addDeal() {
      if (this.new_deals.length >= 4) return
      this.new_deals.push(EMPTY_DEAL())
    },

    removeDeal(index) {
      this.new_deals.splice(index, 1)
    },

    getBackendError(error) {
      const data = error && error.response && error.response.data
      if (!data) return error && error.message ? error.message : ''
      const message = data.response && data.response.message !== undefined ? data.response.message : (data.detail || data.message || data)
      if (typeof message === 'string') return message
      if (Array.isArray(message)) return message.join(', ')
      if (message && typeof message === 'object') {
        return Object.entries(message)
          .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(', ') : typeof value === 'object' ? JSON.stringify(value) : value}`)
          .join(' | ')
      }
      return ''
    },

    appendUserData(fd) {
      fd.append('first_name', this.new_user.first_name.trim())
      fd.append('last_name', this.new_user.last_name.trim())
      fd.append('country', this.new_user.country || '')
      if (this.new_user.avatar) fd.append('avatar', this.new_user.avatar)
    },

    async submit() {
      if (this.submitting) return
      this.submitting = true
      try {
        if (this.proposal_complete) {
          // Caso 1: talento existe y ya tiene imágenes → vincular a agencia
          if (this.needs_user_data) {
            const fd = new FormData()
            fd.append('email', this.talent.email)
            this.appendUserData(fd)
            await talentosService.linkProposal(fd)
          } else {
            await talentosService.linkProposal(this.talent.email)
          }
          this.$bvToast.toast('El talento fue asignado a la agencia correctamente.', {
            title: 'Asignado',
            variant: 'success',
            solid: true,
          })
          this.$router.push({ name: 'talentos' })
        } else if (this.search_status === 'found') {
          // Caso 2: talento existe pero total_images === 0 → crear propuesta
          const fd = new FormData()

          // Talento
          fd.append('email', this.talent.email)
          if (this.needs_user_data) {
            this.appendUserData(fd)
          } else {
            fd.append('first_name', this.talent.first_name)
            fd.append('last_name', this.talent.last_name)
            fd.append('country', this.talent.country || '')
          }
          fd.append('details_proposal_uuid', this.talent.details_proposal_uuid || '')

          // Propuesta
          fd.append('title', this.proposal.title)
          fd.append('description', this.proposal.description)

          // Archivos
          this.samples.forEach(s => {
            if (s.file) fd.append('samples', s.file)
          })

          // Deals como JSON
          const deals_payload = this.new_deals.map(this.dealPayload)
          fd.append('deals', JSON.stringify(deals_payload))

          await talentosService.createProposal(fd)
          this.$bvToast.toast('La propuesta fue guardada correctamente.', {
            title: 'Guardado',
            variant: 'success',
            solid: true,
          })
          // this.$router.push({ name: 'talentos' })
          console.log('se consumio el servicio')
        } else {
          // Caso 3: talento no existe → crear talento y propuesta
          const fd = new FormData()

          // Talento
          fd.append('email', this.new_user.email)
          this.appendUserData(fd)
          fd.append('details_proposal_uuid', '')

          // Propuesta
          fd.append('title', this.proposal.title)
          fd.append('description', this.proposal.description)

          // Archivos
          this.samples.forEach(s => {
            if (s.file) fd.append('samples', s.file)
          })

          // Deals como JSON
          const deals_payload = this.new_deals.map(this.dealPayload)
          fd.append('deals', JSON.stringify(deals_payload))

          await talentosService.createProposal(fd)
          this.$bvToast.toast('El talento y su propuesta fueron creados correctamente.', {
            title: 'Creado',
            variant: 'success',
            solid: true,
          })
          this.$router.push({ name: 'talentos' })
        }
      } catch (e) {
        const backend_error = this.getBackendError(e)
        const message = 'Ocurrió un error, intenta de nuevo.'
        this.$bvToast.toast(backend_error ? `${message} (${backend_error})` : message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.nueva-propuesta-container {
  max-width: 860px;
  margin: 0 auto;
}

.section-card {
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06) !important;
}

.section-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.section-locked {
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
}

.section-header {
  display: flex;
  align-items: center;
}

.step-badge {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--primary, #7367f0);
  color: white;
  font-size: 13px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-found-card {
  display: flex;
  align-items: center;
  background: #f4f8fb;
  border: 1px solid #d0e8f0;
  border-radius: 10px;
  padding: 10px 14px;
}

/* Muestras */
.samples-dropzone {
  border: 2px dashed #d0d5dd;
  border-radius: 10px;
  padding: 28px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: border-color 200ms, background 200ms;

  &:hover, &--drag {
    border-color: var(--primary, #7367f0);
    background: #f5f3ff;
  }
}

.samples-list {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.sample-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
}

.sample-thumb {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  overflow: hidden;
  background: #e5e7eb;
  flex-shrink: 0;
}

.sample-thumb-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Deals */
.deal-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.deal-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f9fafb;
  padding: 8px 14px;
  border-bottom: 1px solid #e5e7eb;
}

.price-range-rating {
  width: max-content;
  padding: 0;
}
.icon-dollar {
  width: 24px;
}
.empty-deals {
  border: 2px dashed #e5e7eb;
  border-radius: 10px;
}

.features-group {
  flex-wrap: wrap;
  gap: 6px;

  ::v-deep .btn {
    border-radius: 20px !important;
    font-size: 12px;
    padding: 4px 12px;
    margin: 0 !important;
  }
}

/* Add network form */
.add-network-form {
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #fafafa;
  margin-bottom: 8px;
}

.add-network-form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px 8px;
  border-bottom: 1px solid #f0f0f0;
}

.add-net-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 14px;
}

.add-net-option {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  transition: border-color 150ms, transform 150ms, box-shadow 150ms;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.12);
  }

  &--selected {
    border-color: transparent;
    box-shadow: 0 0 0 2px #fff, 0 0 0 5px var(--primary, #7367f0);
    transform: scale(1.12);
  }
}

.add-net-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-net-fields {
  display: flex;
  gap: 8px;
  padding: 0 14px 10px;
}

.add-net-actions {
  padding: 8px 14px 12px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f0f0f0;
}

/* Network chip selector */
.network-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
}

.net-chip {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 28px 7px 8px;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 150ms, background 150ms, box-shadow 150ms;
  min-width: 140px;
  max-width: 200px;

  &:hover {
    border-color: var(--primary, #7367f0);
    background: rgba(115, 103, 240, 0.04);
  }

  &--selected {
    border-color: var(--primary, #7367f0);
    background: rgba(115, 103, 240, 0.08);
    box-shadow: 0 0 0 1px var(--primary, #7367f0);
  }
}

.net-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.net-text {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

.net-name {
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
}

.net-user {
  font-size: 11px;
  color: #6e6b7b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.net-check {
  position: absolute;
  top: 5px;
  right: 6px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: var(--primary, #7367f0);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: scale(0.6);
  transition: opacity 150ms, transform 150ms;

  .net-chip--selected & {
    opacity: 1;
    transform: scale(1);
  }

  ::v-deep svg {
    color: white !important;
    stroke: white !important;
  }
}
</style>
